import {
  Button,
  Box,
  HStack,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { mixed, object } from 'yup';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import {
  UploadDataTapeDropzone,
  UploadFileExtension,
} from './UploadDataTapeDropzone';

// Helpers
const configFromFileExtensions = (
  allowedExtensions: UploadFileExtension[],
): { [key: string]: string[] } => {
  const ret: { [key: string]: string[] } = {};
  allowedExtensions.forEach((type) => {
    switch (type) {
      case 'csv':
        ret['text/csv'] = ['.csv'];
        break;
      case 'xls':
        ret['application/vnd.ms-excel'] = ['.xls'];
        break;
      case 'xlsx':
        ret[
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ] = ['.xlsx'];
        break;
      case 'XLSX':
        ret[
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ] = ['.XLSX'];
        break;
      case 'xlsm':
        ret['application/vnd.ms-excel.sheet.macroEnabled.12'] = ['.xlsm'];
        break;
      case 'xlsb':
        ret['application/vnd.ms-excel.sheet.binary.macroEnabled.12'] = [
          '.xlsb',
        ];
        break;
      case 'json':
        ret['application/json'] = ['.json'];
        break;
      case 'pdf':
        ret['application/pdf'] = ['.pdf'];
        break;
      case 'txt':
        ret['text/plain'] = ['.txt', '.TXT'];
        break;
      case 'zip':
        ret['application/zip'] = ['.zip'];
        break;
      case 'html':
        ret['text/html'] = ['.html'];
        break;
      case 'tif':
      case 'tiff':
        ret['image/tiff'] = ['.tif', '.tiff'];
        break;
      case 'jpeg':
      case 'jpg':
        ret['image/jpeg'] = ['.jpeg', '.jpg'];
        break;
      case 'png':
        ret['image/png'] = ['.png'];
        break;
      case 'gz':
        ret['application/gzip'] = ['.gz'];
        break;
      case 'pgp':
        ret['application/pgp'] = ['.pgp'];
        break;
      case 'gpg':
        ret['application/gpg'] = ['.gpg'];
        break;
      default:
        finleyAssertUnreachable(type);
    }
  });
  return ret;
};

// Exports
type Props = {
  allowedExtensions: UploadFileExtension[];
  onClose: () => void;
  handleFile: (file: File) => void;
};
export const UploadDataTapeForm = ({
  allowedExtensions,
  onClose,
  handleFile,
}: Props) => {
  const [loading, setLoading] = useState(false);

  const buttonColorScheme = useColorModeValue('aurora', 'gray');

  return (
    <Formik
      onSubmit={async (values) => {
        try {
          if (values && values.file) {
            handleFile(values.file);
          }
        } catch (err) {
          setLoading(false);
          throw err;
        }
        setLoading(false);
        onClose();
      }}
      validationSchema={object().shape({
        file: mixed(),
      })}
      initialValues={{
        file: null,
      }}
      validateOnChange={false}
    >
      {({ handleSubmit, setFieldValue, values }) => (
        <Form onSubmit={handleSubmit}>
          <VStack spacing="10px">
            <UploadDataTapeDropzone
              allowedExtensions={allowedExtensions}
              isDisabled={loading}
              setFieldValue={(value: FileWithPath) =>
                setFieldValue(`file`, value)
              }
              accept={configFromFileExtensions(allowedExtensions)}
            />
            <Box py="12px" alignSelf="flex-end">
              <HStack spacing="25px">
                <Button
                  variant="outline"
                  colorScheme={buttonColorScheme}
                  onClick={onClose}
                  px="20px"
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  colorScheme={buttonColorScheme}
                  isDisabled={!values.file}
                  isLoading={loading}
                  type="submit"
                  px="20px"
                >
                  Upload
                </Button>
              </HStack>
            </Box>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};
